/**
 * Argus Live — 3-pane authoring editor (AL-B3).
 *
 * Layout (mirrors ToolDeveloperPage but adds the third "live preview"
 * pane between the chat + Monaco — per ARGUS-LIVE-SPEC §5.1):
 *
 *   +-------------------------------------------------+
 *   |  Header — title / status pill / Publish / Save  |
 *   +---------+--------------------------+------------+
 *   |  Chat   |    Live preview iframe   |  Monaco    |
 *   |  panel  |    (AL-A3 endpoint)      |  editor    |
 *   |         |                          |  (HTML /   |
 *   |         |                          |   JS /     |
 *   |         |                          |   CSS)     |
 *   +---------+--------------------------+------------+
 *
 * The Monaco column is hide-able; chat is always visible. Resizable
 * via react-resizable-panels (shared `<ResizablePanelGroup>` primitive).
 *
 * Permission gates (two-layer per ARGUS-LIVE-SPEC §5.5):
 *   - Layer 1: `useHasPermission(<action>, 'argus_live')` — feature
 *     access at the tenant role level.
 *   - Layer 2: `dashboard.effectiveRole` — per-dashboard ACL.
 * Both layers must pass for an action to be enabled; the helpers in
 * `argus-live-helpers.ts` compute the combined affordance booleans.
 *
 * Auto-save:
 *   - Ctrl+S (Cmd+S on Mac) triggers an immediate save.
 *   - A 30s idle timer triggers an auto-save when the draft buffer
 *     differs from the last saved snapshot.
 * Both go through `createVersion` (stages a new draft snapshot, does
 * NOT publish). Publish is an explicit button + the
 * lifecycle:unlock:argus_live permission.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Editor from '@monaco-editor/react';
import {
  ArrowLeft,
  Rocket,
  Save,
  Archive,
  ArchiveRestore,
  Code as CodeIcon,
  RefreshCw,
  PanelRightClose,
  PanelRight,
  ExternalLink,
  Loader2,
  Share2,
} from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  RUNTIME_PROTOCOL_VERSION,
  type HostToIframeEvent,
} from '@argusai/ai-core/argus-live/runtime/post-message-protocol';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import { StatusPill } from '@/components/ui/status-pill';
import { useTheme } from '@/components/theme-provider';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import {
  type ArgusLiveDashboardWithVersion,
  type ArgusLiveVersion,
  useCreateArgusLiveVersion,
  usePublishArgusLiveDashboard,
  useArchiveArgusLiveDashboard,
  useUnarchiveArgusLiveDashboard,
} from '@/lib/use-argus-live';
import type { SubscribeBindingSummary } from '@/lib/use-argus-live-subscribe';
import {
  AUTO_SAVE_IDLE_MS,
  computeAffordances,
  formatDataBindingsJson,
  formatVersionLabel,
  monacoLanguageForTab,
  parseDataBindingsJson,
  shouldAutoSave,
  statusToneFor,
  type ArgusLivePermissionFlags,
  type CodeTab,
} from '@/lib/argus-live-helpers';
import { useArgusLiveIframeToken } from '@/lib/use-argus-live-iframe-token';

import { parseCockpitSpec, type CockpitSpec } from '@argusai/shared';
import { ArgusLiveChatPanel } from './argus-live-chat-panel';
import { ArgusLivePostMessageHost } from './post-message-host';
import { ArgusLiveShareDialog } from './argus-live-share-dialog';
import { CockpitSpecEditor } from './cockpit/cockpit-spec-editor';
import {
  readCockpitSpecFromSandbox,
  emptyCockpitSpec,
} from './cockpit/cockpit-editor-model';
import type { ExportControl, Sensitivity } from '@argusai/shared';

// ─── Props ───────────────────────────────────────────────────────────────

export interface ArgusLiveEditorProps {
  /** Server response from `useArgusLiveDashboard(id)`. */
  dashboardData: ArgusLiveDashboardWithVersion;
  /** All versions; populates the version dropdown. */
  versions: ArgusLiveVersion[];
  /** Permission flags from `useHasPermission`. */
  permissions: ArgusLivePermissionFlags;
  /** Currently displayed version id (null = current draft). */
  activeVersionId: string | null;
  /** Callback when the user switches versions via the dropdown. */
  onChangeActiveVersion: (versionId: string | null) => void;
}

// ─── Component ───────────────────────────────────────────────────────────

type RuntimeReadyPayload = Omit<
  Extract<HostToIframeEvent, { type: 'READY' }>,
  'type'
>;

function runtimeBindingsFromManifest(
  manifest: unknown[],
): RuntimeReadyPayload['bindings'] {
  return manifest.flatMap((raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const b = raw as Record<string, unknown>;
    const id = typeof b.id === 'string' ? b.id.trim() : '';
    const name =
      typeof b.name === 'string' && b.name.trim().length > 0
        ? b.name.trim().slice(0, 200)
        : id;
    if (!id || id.length > 120 || !name) return [];
    const hint =
      b.classificationHint &&
      typeof b.classificationHint === 'object' &&
      !Array.isArray(b.classificationHint)
        ? (b.classificationHint as {
            sensitivity?: unknown;
            exportControl?: unknown;
          })
        : {};
    const sensitivity: Sensitivity =
      hint.sensitivity === 'cui' || hint.sensitivity === 'restricted'
        ? hint.sensitivity
        : 'unclassified';
    const exportControl: ExportControl =
      hint.exportControl === 'itar' || hint.exportControl === 'ear'
        ? hint.exportControl
        : 'none';
    const refreshIntervalMs =
      typeof b.refreshIntervalMs === 'number' &&
      Number.isFinite(b.refreshIntervalMs) &&
      b.refreshIntervalMs >= 5_000 &&
      b.refreshIntervalMs <= 86_400_000
        ? Math.trunc(b.refreshIntervalMs)
        : null;
    return [
      {
        id,
        name,
        classificationHint: { sensitivity, exportControl },
        refreshIntervalMs,
      },
    ];
  });
}

function subscribeBindingsFromManifest(
  manifest: unknown[],
): SubscribeBindingSummary[] {
  return manifest.flatMap((raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const b = raw as Record<string, unknown>;
    const id = typeof b.id === 'string' ? b.id.trim() : '';
    const refreshMode =
      typeof b.refreshMode === 'string' ? b.refreshMode : 'manual';
    if (!id || id.length > 120) return [];
    const tables = Array.isArray(b.tables)
      ? b.tables.filter((x): x is string => typeof x === 'string')
      : undefined;
    const params =
      b.params && typeof b.params === 'object' && !Array.isArray(b.params)
        ? (b.params as Record<string, unknown>)
        : undefined;
    return [{ id, refreshMode, tables, params }];
  });
}

/**
 * Mode dispatcher.
 *
 * Cockpit cockpits render from a CockpitSpec persisted on the version's
 * `sandbox_config_jsonb.cockpitSpec` — there is NO raw HTML/JS/CSS for them. The
 * visual SPEC editor (`CockpitSpecEditor`) is the authoring experience for those
 * (and the default for brand-new cockpits, so a user can actually BUILD a
 * CockpitSpec — the readiness audit's #1 blocker). Legacy raw-HTML cockpits keep
 * the Monaco-based editor.
 *
 * Detection (the displayed version wins, then the dashboard's current version):
 *   - the active/current version carries a valid `cockpitSpec`  → spec editor
 *   - a brand-new dashboard with NO version at all              → spec editor
 *   - otherwise (existing raw HTML/JS/CSS)                       → HTML editor
 */
export function ArgusLiveEditor(props: ArgusLiveEditorProps) {
  const { dashboardData, versions, activeVersionId, permissions } = props;
  const dashboard = dashboardData.dashboard;

  // The version whose content is currently displayed (dropdown selection wins).
  const displayedVersion =
    activeVersionId != null
      ? versions.find((v) => v.id === activeVersionId) ?? dashboardData.currentVersion
      : dashboardData.currentVersion;

  const { isSpec, seedSpec } = useMemo(() => {
    const rawSpec = readCockpitSpecFromSandbox(
      displayedVersion?.sandboxConfig as Record<string, unknown> | null | undefined,
    );
    if (rawSpec !== undefined) {
      const parsed = parseCockpitSpec(rawSpec);
      if (parsed.ok && parsed.spec) return { isSpec: true, seedSpec: parsed.spec };
      // A cockpitSpec key exists but is invalid — still treat as a spec cockpit
      // (start the visual editor from a fresh container titled after the
      // dashboard) rather than dropping the user into the raw-HTML editor.
      return { isSpec: true, seedSpec: emptyCockpitSpec(dashboard.title) };
    }
    // Brand-new dashboard with no version at all → default to the spec editor.
    if (!displayedVersion && versions.length === 0) {
      return { isSpec: true, seedSpec: emptyCockpitSpec(dashboard.title) };
    }
    return { isSpec: false, seedSpec: null as CockpitSpec | null };
  }, [displayedVersion, versions.length, dashboard.title]);

  const effectiveRole = dashboardData.effectiveRole;
  const affordances = useMemo(
    () => computeAffordances(permissions, dashboard, effectiveRole),
    [permissions, dashboard, effectiveRole],
  );

  if (isSpec && seedSpec) {
    // Suggest the next minor rev bump (mirrors the HTML editor's seed logic).
    const seedRev = displayedVersion?.revLabel ?? '1.0';
    const [maj, min] = seedRev.split('.').map((n) => parseInt(n, 10) || 0);
    const nextRev = `${maj}.${(min || 0) + 1}`;
    return (
      <CockpitSpecEditor
        dashboard={dashboard}
        seedVersion={displayedVersion ?? null}
        initialSpec={seedSpec}
        affordances={affordances}
        initialRevLabel={nextRev}
      />
    );
  }

  return <ArgusLiveHtmlEditor {...props} />;
}

function ArgusLiveHtmlEditor({
  dashboardData,
  versions,
  permissions,
  activeVersionId,
  onChangeActiveVersion,
}: ArgusLiveEditorProps) {
  const navigate = useNavigate();
  const { mode } = useTheme();
  const dashboard = dashboardData.dashboard;
  const currentVersion = dashboardData.currentVersion;
  const effectiveRole = dashboardData.effectiveRole;

  const affordances = useMemo(
    () => computeAffordances(permissions, dashboard, effectiveRole),
    [permissions, dashboard, effectiveRole],
  );

  // ── Draft buffer (independent of the active version display) ──────────
  // Initial: the current version contents (or empty for brand-new dashboards).
  const seed = currentVersion;
  const [draftHtml, setDraftHtml] = useState(seed?.codeHtml ?? '');
  const [draftJs, setDraftJs] = useState(seed?.codeJs ?? '');
  const [draftCss, setDraftCss] = useState(seed?.codeCss ?? '');
  const [draftBindingsJson, setDraftBindingsJson] = useState(() =>
    formatDataBindingsJson(seed?.dataBindings),
  );
  const [draftRevLabel, setDraftRevLabel] = useState<string>(() => {
    if (!seed) return '1.0';
    // Suggest the next minor bump
    const [maj, min] = seed.revLabel.split('.').map((n) => parseInt(n, 10) || 0);
    return `${maj}.${(min || 0) + 1}`;
  });
  const [lastSaved, setLastSaved] = useState<
    | {
        html: string;
        js: string;
        css: string;
        revLabel: string;
        dataBindingsJson: string;
      }
    | null
  >(seed ? {
    html: seed.codeHtml,
    js: seed.codeJs,
    css: seed.codeCss,
    revLabel: seed.revLabel,
    dataBindingsJson: formatDataBindingsJson(seed.dataBindings),
  } : null);

  // ── UI state ──────────────────────────────────────────────────────────
  const [codeTab, setCodeTab] = useState<CodeTab>('html');
  const [editorHidden, setEditorHidden] = useState(false);
  const [iframeRefreshNonce, setIframeRefreshNonce] = useState(0);
  const [isSavingNow, setIsSavingNow] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Re-seed the buffer when the user switches versions in the dropdown.
  // Switching to `null` means "show current draft" (the in-memory buffer).
  useEffect(() => {
    if (activeVersionId == null) return;
    const v = versions.find((x) => x.id === activeVersionId);
    if (!v) return;
    setDraftHtml(v.codeHtml);
    setDraftJs(v.codeJs);
    setDraftCss(v.codeCss);
    setDraftBindingsJson(formatDataBindingsJson(v.dataBindings));
    setDraftRevLabel(v.revLabel);
    setLastSaved({
      html: v.codeHtml,
      js: v.codeJs,
      css: v.codeCss,
      revLabel: v.revLabel,
      dataBindingsJson: formatDataBindingsJson(v.dataBindings),
    });
  }, [activeVersionId, versions]);

  // ── Mutations ─────────────────────────────────────────────────────────
  const createVersion = useCreateArgusLiveVersion();
  const publishMutation = usePublishArgusLiveDashboard();
  const archiveMutation = useArchiveArgusLiveDashboard();
  const unarchiveMutation = useUnarchiveArgusLiveDashboard();

  const saveDraft = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!affordances.canSaveDraft) return;
      if (isSavingNow) return;
      let dataBindings: unknown[];
      try {
        dataBindings = parseDataBindingsJson(draftBindingsJson);
      } catch (err) {
        toast.error((err as Error).message);
        return;
      }
      setIsSavingNow(true);
      try {
        const formattedDataBindingsJson = formatDataBindingsJson(dataBindings);
        const newVersion = await createVersion.mutateAsync({
          dashboardId: dashboard.id,
          revLabel: draftRevLabel,
          codeHtml: draftHtml,
          codeJs: draftJs,
          codeCss: draftCss,
          dataBindings,
        });
        setDraftBindingsJson(formattedDataBindingsJson);
        setLastSaved({
          html: draftHtml,
          js: draftJs,
          css: draftCss,
          revLabel: draftRevLabel,
          dataBindingsJson: formattedDataBindingsJson,
        });
        // S3 P1 #6 fix: do NOT flip the version dropdown to the
        // just-saved draft. The previous behaviour (calling
        // `onChangeActiveVersion(savedVersionId)` after every save)
        // yanked the dropdown out from under the user mid-edit —
        // they'd be typing in "Current draft", press Ctrl+S, and the
        // selector would jump to "v1.2 — Mar 18 14:32" which silently
        // re-mounts the editor and re-seeds the buffer on the next
        // versions[] refetch. The user's edit context shouldn't get
        // hijacked by background save state.
        //
        // Instead: stay on "Current draft" (activeVersionId=null) so
        // the user keeps editing the live buffer. The dropdown will
        // only flip to a labelled version on an explicit Publish or
        // when the user picks one from the dropdown themselves.
        //
        // The preview iframe still refreshes (next line) because it
        // reads from dashboard.currentVersionId, which the parent
        // route refetches via useArgusLiveDashboard's invalidation —
        // and we bump iframeRefreshNonce to force a reload.
        void newVersion; // intentionally unused
        setIframeRefreshNonce((n) => n + 1);
        if (!opts?.silent) toast.success('Draft saved');
      } catch (err) {
        toast.error(`Save failed: ${(err as Error).message}`);
      } finally {
        setIsSavingNow(false);
      }
    },
    [
      affordances.canSaveDraft,
      isSavingNow,
      createVersion,
      dashboard.id,
      draftRevLabel,
      draftHtml,
      draftJs,
      draftCss,
      draftBindingsJson,
    ],
  );

  // ── Auto-save (30s idle) ──────────────────────────────────────────────
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    const need = shouldAutoSave({
      draft: {
        html: draftHtml,
        js: draftJs,
        css: draftCss,
        revLabel: draftRevLabel,
        dataBindingsJson: draftBindingsJson,
      },
      lastSaved,
      canSaveDraft: affordances.canSaveDraft,
      isSavingNow,
    });
    if (!need) return;
    autoSaveTimerRef.current = setTimeout(() => {
      void saveDraft({ silent: true });
    }, AUTO_SAVE_IDLE_MS);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [
    draftHtml,
    draftJs,
    draftCss,
    draftBindingsJson,
    draftRevLabel,
    lastSaved,
    affordances.canSaveDraft,
    isSavingNow,
    saveDraft,
  ]);

  // ── Ctrl+S / Cmd+S handler at the route level (Monaco also wires it) ──
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        void saveDraft();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [saveDraft]);

  // ── Publish ───────────────────────────────────────────────────────────
  const publish = useCallback(async () => {
    if (!affordances.canPublish) return;
    let dataBindings: unknown[];
    try {
      dataBindings = parseDataBindingsJson(draftBindingsJson);
    } catch (err) {
      toast.error((err as Error).message);
      return;
    }
    try {
      const formattedDataBindingsJson = formatDataBindingsJson(dataBindings);
      await publishMutation.mutateAsync({
        dashboardId: dashboard.id,
        revLabel: draftRevLabel,
        codeHtml: draftHtml,
        codeJs: draftJs,
        codeCss: draftCss,
        dataBindings,
      });
      setDraftBindingsJson(formattedDataBindingsJson);
      setLastSaved({
        html: draftHtml,
        js: draftJs,
        css: draftCss,
        revLabel: draftRevLabel,
        dataBindingsJson: formattedDataBindingsJson,
      });
      toast.success(`Published v${draftRevLabel}`);
    } catch (err) {
      toast.error(`Publish failed: ${(err as Error).message}`);
    }
  }, [
    affordances.canPublish,
    publishMutation,
    dashboard.id,
    draftRevLabel,
    draftHtml,
    draftJs,
    draftCss,
    draftBindingsJson,
  ]);

  // ── Archive / Unarchive ───────────────────────────────────────────────
  const onArchive = useCallback(async () => {
    if (!affordances.canArchive) return;
    try {
      await archiveMutation.mutateAsync({ dashboardId: dashboard.id });
      toast.success('Dashboard archived');
    } catch (err) {
      toast.error(`Archive failed: ${(err as Error).message}`);
    }
  }, [affordances.canArchive, archiveMutation, dashboard.id]);

  const onUnarchive = useCallback(async () => {
    if (!affordances.canUnarchive) return;
    try {
      await unarchiveMutation.mutateAsync({ dashboardId: dashboard.id });
      toast.success('Dashboard restored');
    } catch (err) {
      toast.error(`Unarchive failed: ${(err as Error).message}`);
    }
  }, [affordances.canUnarchive, unarchiveMutation, dashboard.id]);

  // ── Preview source URL ────────────────────────────────────────────────
  // Preview always uses the currently-published version (currentVersionId).
  // While the user has unsaved drafts, the preview displays the previously
  // saved version — the live-preview-of-draft path goes through
  // createVersion (auto-save) and re-renders via the iframeRefreshNonce.
  //
  // R4 P0: the iframe runs in a null-origin sandbox so it CANNOT send
  // `Authorization: Bearer` headers from the parent SPA's
  // `localStorage`. The `useArgusLiveIframeToken` hook mints a 5-min
  // signed JWT scoped to (dashboardId, versionId) and rotates it 30s
  // before expiry, then appends it to the URL as `?token=<jwt>`. The
  // server's iframe-content route accepts this token as a
  // Bearer-equivalent.
  const previewVersionId =
    activeVersionId ?? dashboard.currentVersionId ?? null;
  const previewVersion =
    activeVersionId != null
      ? versions.find((x) => x.id === activeVersionId) ?? null
      : currentVersion;
  const previewBindings = useMemo(
    () =>
      Array.isArray(previewVersion?.dataBindings)
        ? previewVersion.dataBindings
        : [],
    [previewVersion],
  );
  const previewReadyPayload = useMemo<RuntimeReadyPayload>(
    () => ({
      protocolVersion: RUNTIME_PROTOCOL_VERSION,
      bindings: runtimeBindingsFromManifest(previewBindings),
      classification: {
        sensitivity:
          previewVersion?.classificationTag.sensitivity ??
          dashboard.classificationTag.sensitivity,
        exportControl:
          previewVersion?.classificationTag.exportControl ??
          dashboard.classificationTag.exportControl,
      },
      theme: {
        mode: mode === 'dark' ? 'dark' : 'light',
        primaryColor: '#1890ff',
        surfaceColor: mode === 'dark' ? '#020817' : '#ffffff',
      },
    }),
    [dashboard.classificationTag, mode, previewBindings, previewVersion],
  );
  const previewSubscribeBindings = useMemo(
    () => subscribeBindingsFromManifest(previewBindings),
    [previewBindings],
  );
  const { iframeSrc, error: iframeTokenError } = useArgusLiveIframeToken({
    dashboardId: dashboard.id,
    versionId: previewVersionId,
    refreshNonce: iframeRefreshNonce,
  });

  // ── Render ────────────────────────────────────────────────────────────
  const codeValue =
    codeTab === 'html'
      ? draftHtml
      : codeTab === 'js'
        ? draftJs
        : codeTab === 'css'
          ? draftCss
          : draftBindingsJson;
  const setCodeValue = (v: string) => {
    if (codeTab === 'html') setDraftHtml(v);
    else if (codeTab === 'js') setDraftJs(v);
    else if (codeTab === 'css') setDraftCss(v);
    else setDraftBindingsJson(v);
  };

  const versionDropdownLabel =
    activeVersionId == null
      ? 'Current draft'
      : (() => {
          const v = versions.find((x) => x.id === activeVersionId);
          return v ? formatVersionLabel(v) : 'Unknown version';
        })();

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="border-b border-border/40 px-4 py-2.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => navigate({ to: '/argus-live' })}
            aria-label="Back to dashboards"
            title="Back to dashboards"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <div className="flex items-center gap-2 min-w-0">
            <h1
              className="text-sm font-semibold truncate"
              title={dashboard.title}
            >
              {dashboard.title}
            </h1>
            <StatusPill status={statusToneFor(dashboard.status)} compact>
              {dashboard.status}
            </StatusPill>
            <Badge
              variant="outline"
              className="text-[9px] font-mono"
              title="Slug — stable id used in URLs and exports"
            >
              {dashboard.slug}
            </Badge>
            {affordances.isReadOnly && (
              <Badge
                variant="outline"
                className="text-[9px] border-amber-500/30 text-amber-700 dark:text-amber-400"
              >
                read-only
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Version dropdown */}
          <Select
            value={activeVersionId ?? '__current__'}
            onValueChange={(v) =>
              onChangeActiveVersion(v === '__current__' ? null : v)
            }
          >
            <SelectTrigger
              className="h-8 text-xs gap-1.5 min-w-[180px]"
              aria-label="Switch dashboard version"
            >
              <SelectValue placeholder="Pick a version">
                {versionDropdownLabel}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__current__" className="text-xs">
                Current draft
              </SelectItem>
              {versions.map((v) => (
                <SelectItem key={v.id} value={v.id} className="text-xs">
                  {formatVersionLabel(v)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setEditorHidden((x) => !x)}
            title={editorHidden ? 'Show code editor' : 'Hide code editor'}
            aria-label={editorHidden ? 'Show editor' : 'Hide editor'}
          >
            {editorHidden ? (
              <PanelRight className="h-4 w-4" />
            ) : (
              <PanelRightClose className="h-4 w-4" />
            )}
          </Button>

          {permissions.canShare && (
            <Button
              variant="outline"
              size="sm"
              disabled={!affordances.canShare}
              onClick={() => setShareOpen(true)}
              title="Share this dashboard"
              data-testid="argus-live-editor-share-btn"
            >
              <Share2 className="h-3.5 w-3.5 mr-1.5" />
              Share
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            disabled={!affordances.canSaveDraft || isSavingNow}
            onClick={() => void saveDraft()}
            title="Save draft (Ctrl+S)"
          >
            {isSavingNow ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            Save Draft
          </Button>

          {dashboard.status === 'archived' ? (
            <Button
              variant="outline"
              size="sm"
              disabled={!affordances.canUnarchive}
              onClick={() => void onUnarchive()}
            >
              <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" />
              Restore
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={!affordances.canArchive}
                onClick={() => void onArchive()}
              >
                <Archive className="h-3.5 w-3.5 mr-1.5" />
                Archive
              </Button>
              <Button
                size="sm"
                disabled={!affordances.canPublish || publishMutation.isPending}
                onClick={() => void publish()}
              >
                {publishMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Rocket className="h-3.5 w-3.5 mr-1.5" />
                )}
                Publish
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── Body: 3 (or 2) panes ───────────────────────────────────── */}
      <div className="flex-1 min-h-0">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Chat */}
          {/*
              R4-followup: `react-resizable-panels` v2→v4 silently
              changed bare-number semantics: `28` now means "28 pixels",
              not "28 percent". The original PR #484 CSS fix
              (`min-w-0`) couldn't help because the LIBRARY itself was
              computing `flex: 2.443 1 0px` inline (45px maxSize hard-
              capping the chat panel). Passing percent STRINGS forces
              v4 to treat the numbers as percentages, as v2 did.
              `min-w-0` is kept for defense in depth.
           */}
          <ResizablePanel
            defaultSize="28%"
            minSize="25%"
            maxSize="60%"
            className="min-w-0"
          >
            <ArgusLiveChatPanel
              dashboardId={dashboard.id}
              disabled={affordances.isReadOnly}
              bannerMarkdown={
                affordances.isReadOnly
                  ? '_You have view-only access to this dashboard. Authoring is disabled._'
                  : undefined
              }
              // Use the dedicated authoring helper. V1 keeps application
              // explicit through the editor tabs; auto-apply mutation cards
              // are intentionally not claimed here yet.
              targetAgentSlug="system.argus_live_author"
            />
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Preview */}
          <ResizablePanel defaultSize={editorHidden ? '72%' : '42%'} minSize="25%">
            <div className="flex flex-col h-full">
              <div className="border-b border-border/40 px-3 py-1.5 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="font-medium">Live preview</span>
                  {previewVersionId && (
                    <span className="font-mono text-[10px]" title="Version id">
                      {previewVersionId.slice(0, 8)}…
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setIframeRefreshNonce((n) => n + 1)}
                    title="Refresh preview"
                    aria-label="Refresh preview"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                  {iframeSrc && (
                    <a
                      href={iframeSrc}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label="Open preview in new tab"
                      title="Open preview in new tab"
                      className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-accent text-muted-foreground hover:text-accent-foreground"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
              <div className="flex-1 min-h-0 bg-muted/20">
                {iframeSrc ? (
                  <ArgusLivePostMessageHost
                    dashboardId={dashboard.id}
                    versionId={previewVersionId}
                    title={`Argus Live preview — ${dashboard.title}`}
                    src={iframeSrc}
                    className="w-full h-full border-0"
                    fillContainer
                    iframeTestId="argus-live-preview-iframe"
                    subscribeBindings={previewSubscribeBindings}
                    readyPayload={previewReadyPayload}
                  />
                ) : iframeTokenError && previewVersionId ? (
                  // R4 P0: distinct empty-state when the token mint
                  // failed (e.g. user lost their grant mid-session,
                  // version got deleted, server returned 5xx).
                  <div className="h-full flex flex-col items-center justify-center text-center p-6">
                    <CodeIcon className="h-10 w-10 text-amber-500/40 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">
                      Preview unavailable
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1 max-w-sm">
                      {iframeTokenError}
                    </p>
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center p-6">
                    <CodeIcon className="h-10 w-10 text-muted-foreground/30 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">
                      No published version yet
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1 max-w-sm">
                      Save a draft to render it here, or publish to make it
                      visible to your tenant.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </ResizablePanel>

          {!editorHidden && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize="30%" minSize="20%">
                <div className="flex flex-col h-full">
                  {/* Tab bar + rev label */}
                  <div className="border-b border-border/40 px-3 py-1.5 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-1">
                      {(['html', 'js', 'css', 'bindings'] as CodeTab[]).map((t) => (
                        <Button
                          key={t}
                          variant={codeTab === t ? 'secondary' : 'ghost'}
                          size="sm"
                          className="h-7 text-xs font-mono px-3"
                          onClick={() => setCodeTab(t)}
                          aria-pressed={codeTab === t}
                          aria-label={`${t === 'bindings' ? 'BINDINGS' : t.toUpperCase()} tab`}
                        >
                          {t === 'bindings' ? 'BINDINGS' : t.toUpperCase()}
                        </Button>
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">rev</span>
                      <input
                        aria-label="Revision label"
                        type="text"
                        className="h-6 w-16 px-2 text-xs font-mono rounded border border-input bg-background"
                        value={draftRevLabel}
                        onChange={(e) => setDraftRevLabel(e.target.value)}
                        disabled={affordances.isReadOnly}
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-h-0">
                    <Editor
                      height="100%"
                      language={monacoLanguageForTab(codeTab)}
                      value={codeValue}
                      onChange={(v) => setCodeValue(v ?? '')}
                      theme={mode === 'dark' ? 'vs-dark' : 'light'}
                      onMount={(editor, monaco) => {
                        editor.addCommand(
                          monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
                          () => void saveDraft(),
                        );
                      }}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 12,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        tabSize: 2,
                        readOnly: affordances.isReadOnly,
                        padding: { top: 8, bottom: 8 },
                        bracketPairColorization: { enabled: true },
                        renderLineHighlight: 'line',
                      }}
                    />
                  </div>
                  <div
                    className={cn(
                      'px-3 py-1 border-t border-border/40 flex items-center justify-between shrink-0 bg-card text-[10px]',
                      isSavingNow ? 'text-primary' : 'text-muted-foreground',
                    )}
                  >
                    <span>
                      {isSavingNow
                        ? 'Saving…'
                        : lastSaved
                          ? 'All changes saved'
                          : 'Unsaved draft'}
                    </span>
                    <span>Ctrl+S to save · auto-save every 30s</span>
                  </div>
                </div>
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </div>

      {/* Status footer link back to the list */}
      <div className="border-t border-border/40 px-3 py-1.5 flex items-center justify-between shrink-0 text-[10px] text-muted-foreground bg-card">
        <span>
          Owner: <span className="font-mono">{dashboard.ownerUserId.slice(0, 8)}…</span>
          {' · '}Updated {new Date(dashboard.updatedAt).toLocaleString()}
        </span>
        <Link
          to="/argus-live"
          className="hover:text-foreground underline-offset-2 hover:underline"
        >
          Back to dashboards
        </Link>
      </div>

      {/* Share dialog — mount unconditionally so close-animations work */}
      {permissions.canShare && (
        <ArgusLiveShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          dashboardId={dashboard.id}
          dashboardTitle={dashboard.title}
          canMutate={affordances.canShare}
        />
      )}
    </div>
  );
}
